import { Archive, ArrowLeft, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";

import { api } from "@/api";
import { useAuth } from "@/auth";
import { ActivityFeed } from "@/components/projects/ActivityFeed";
import { Avatar } from "@/components/projects/common";
import { DiscussionsTab } from "@/components/projects/DiscussionsTab";
import { DocsTab } from "@/components/projects/DocsTab";
import { FilesTab } from "@/components/projects/FilesTab";
import { MembersTab } from "@/components/projects/MembersTab";
import { TodosTab } from "@/components/projects/TodosTab";
import "@/projects.css";

const TABS = [["todos", "Tâches à faire"], ["activite", "Activité"], ["discussions", "Discussions"], ["documents", "Documents"], ["fichiers", "Fichiers"], ["membres", "Membres"]];

export default function ProjectPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [project, setProject] = useState(null);
  const tab = params.get("tab") || "todos";
  const load = useCallback(() => api.get(`/projects/${id}`).then(({ data }) => setProject(data)).catch(() => navigate("/projets", { replace: true })), [id, navigate]);
  useEffect(() => { load(); }, [load]);
  if (!project) return <section className="page"><p className="pj-empty">Chargement…</p></section>;
  const members = project.members_info;
  const open = (t, type, itemId) => setParams(type && itemId ? { tab: t, [type]: itemId } : { tab: t });
  const internal = user.role !== "guest";
  const archive = async () => { await api.patch(`/projects/${id}`, { archived: !project.archived }); toast.success(project.archived ? "Projet restauré." : "Projet archivé."); load(); };
  const remove = async () => { if (window.confirm("Supprimer définitivement ce projet et tout son contenu ?")) { await api.delete(`/projects/${id}`); navigate("/projets"); } };
  return (
    <section className="page pj-page" data-testid="project-page">
      <Link to="/projets" className="pj-back" data-testid="project-back"><ArrowLeft size={14} />Tous les projets</Link>
      <header className="pj-head">
        <div>
          <h1 data-testid="project-name">{project.name}{project.archived && <span className="pj-pill">Archivé</span>}</h1>
          {project.description && <p className="page-subtitle">{project.description}</p>}
        </div>
        <div className="pj-head-side">
          <div className="pj-avatars" data-testid="project-avatars">{members.slice(0, 8).map((m) => <Avatar key={m.id} name={m.name} size={32} />)}</div>
          {internal && <button type="button" className="secondary-button" onClick={archive} data-testid="project-archive"><Archive size={15} />{project.archived ? "Restaurer" : "Archiver"}</button>}
          {internal && (user.role === "admin" || project.created_by === user.id) && <button type="button" className="secondary-button" onClick={remove} data-testid="project-delete"><Trash2 size={15} /></button>}
        </div>
      </header>
      <nav className="pj-tabs" data-testid="project-tabs">{TABS.map(([k, label]) => <button key={k} type="button" className={tab === k ? "is-on" : ""} onClick={() => open(k)} data-testid={`project-tab-${k}`}>{label}</button>)}</nav>
      <div className="pj-tab-body">
        {tab === "activite" && <ActivityFeed project={project} onOpen={open} />}
        {tab === "todos" && <TodosTab project={project} members={members} openId={params.get("todo")} onRenamed={load} />}
        {tab === "discussions" && <DiscussionsTab project={project} members={members} openId={params.get("message")} />}
        {tab === "documents" && <DocsTab project={project} members={members} openId={params.get("doc")} />}
        {tab === "fichiers" && <FilesTab project={project} />}
        {tab === "membres" && <MembersTab project={project} onChanged={load} />}
      </div>
    </section>
  );
}
